import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Check, ChevronDown, Globe, Search, X } from 'lucide-react';
import { storeLanguagePreference } from '../utils/languageStorage';
import './componentDarkSurfaceFixes.css';

type LanguageSwitcherVariant = 'dropdown' | 'panel';

interface LanguageSwitcherProps {
  variant?: LanguageSwitcherVariant;
  onAfterChange?: () => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'dropdown',
  onAfterChange,
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [open, setOpen] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const languageSearchRef = useRef<HTMLInputElement>(null);

  const allLanguages = useMemo(
    () => [
      { code: 'en', label: 'English' },
      { code: 'cn', label: '简体中文' },
      { code: 'zh', label: '繁體中文' },
      { code: 'de', label: 'Deutsch' },
      { code: 'es', label: 'Español' },
      { code: 'pl', label: 'Polski' },
      { code: 'hi', label: 'हिन्दी' },
      { code: 'ta', label: 'தமிழ்' },
      { code: 'te', label: 'తెలుగు' },
      { code: 'ne', label: 'नेपाली' },
      { code: 'he', label: 'עברית' },
      { code: 'fr', label: 'Français' },
      { code: 'ja', label: '日本語' },
      { code: 'pt', label: 'Português' },
      { code: 'id', label: 'Bahasa Indonesia' },
      { code: 'ru', label: 'Русский' },
      { code: 'nl', label: 'Nederlands' },
    ],
    []
  );

  const resolveCurrentLang = (raw: string | undefined): string => {
    const lower = (raw || '').toLowerCase();
    if (lower.startsWith('zh')) {
      const isTraditional =
        lower.includes('zh-hant') ||
        lower.includes('-tw') ||
        lower.includes('-hk') ||
        lower.includes('-mo');
      return isTraditional ? 'zh' : 'cn';
    }
    const base = lower.split('-')[0];
    return base || 'en';
  };

  const currentLang = (() => {
    const resolved = resolveCurrentLang(i18n.language) || resolveCurrentLang(lang) || 'en';
    return allLanguages.some((l) => l.code === resolved) ? resolved : 'en';
  })();

  const currentLabel = allLanguages.find((l) => l.code === currentLang)?.label || 'English';

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    // Add event listener only when dropdown is open
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]); // Re-run this effect when open state changes

  const orderedLanguages = useMemo(
    () => [
      ...allLanguages.filter((l) => l.code === currentLang),
      ...allLanguages.filter((l) => l.code !== currentLang),
    ],
    [allLanguages, currentLang]
  );

  const filteredLanguages = useMemo(() => {
    const q = languageSearch.trim().toLowerCase();
    if (!q) return orderedLanguages;
    return orderedLanguages.filter(
      (l) => l.label.toLowerCase().includes(q) || l.code.toLowerCase().includes(q)
    );
  }, [languageSearch, orderedLanguages]);

  useEffect(() => {
    if (!open || variant !== 'panel') return;
    setTimeout(() => languageSearchRef.current?.focus(), 75);
  }, [open, variant]);

  const changeLanguage = (newLang: string) => {
    const supportedCodes = allLanguages.map((l) => l.code).join('|');
    const langPrefixRegex = new RegExp(`^\\/(${supportedCodes})(?=\\/|$)`);

    // Get current path without language prefix and normalize leading slash
    const stripped = location.pathname.replace(langPrefixRegex, '');
    const currentPath = stripped ? (stripped.startsWith('/') ? stripped : `/${stripped}`) : '/';
    
    // Store language preference as manual selection
    storeLanguagePreference(newLang, true);
    
    // Change i18next language immediately
    i18n.changeLanguage(newLang);
    
    // Navigate to new language URL
    const newPath = `/${newLang}${currentPath}`;
    navigate(newPath);
    
    setOpen(false);
    setLanguageSearch('');
    onAfterChange?.();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className={
          variant === 'panel'
            ? "sl-language-trigger w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded-lg border border-transparent hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            : "sl-language-trigger flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded-lg border border-transparent hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          <Globe className="w-4 h-4 flex-shrink-0" />
          <span className="truncate" style={{ fontSize: '13px' }}>{currentLabel}</span>
        </span>
        <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        variant === 'panel' ? (
          <div className="sl-language-panel mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-2 space-y-2">
            <div className="sl-language-search flex items-center gap-2 px-2 py-2 rounded-md bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
              <Search className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <input
                ref={languageSearchRef}
                type="text"
                value={languageSearch}
                onChange={(e) => setLanguageSearch(e.target.value)}
                placeholder="Search languages..."
                className="w-full bg-transparent outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
              />
              {languageSearch ? (
                <button
                  type="button"
                  onClick={() => setLanguageSearch('')}
                  className="sl-language-search-clear p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>

            {filteredLanguages.length > 0 ? (
              <div className="max-h-56 overflow-y-auto grid grid-cols-2 gap-2 pr-1">
                {filteredLanguages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => changeLanguage(language.code)}
                    className={`sl-language-item flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition ${
                      language.code === currentLang
                        ? 'sl-language-item-active bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                    title={language.label}
                    type="button"
                  >
                    <span className="truncate">{language.label}</span>
                    {language.code === currentLang ? (
                      <Check className="w-4 h-4 flex-shrink-0" />
                    ) : null}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
                No languages found for "{languageSearch}"
              </div>
            )}
          </div>
        ) : (
          <div className="sl-language-panel absolute right-0 mt-2 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
            {orderedLanguages.map((language) => (
              <button 
                key={language.code}
                onClick={() => changeLanguage(language.code)} 
                className={`sl-language-item block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  language.code === currentLang ? 'sl-language-item-active bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-300' : ''
                }`}
                type="button"
              >
                {language.label}
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default LanguageSwitcher;
